import {
  IconBusinessMulticolor,
  IconCarMulticolor,
  IconChairMulticolor,
  IconHobbyMulticolor,
  IconHouseMulticolor,
  IconJobMulticolor,
  IconOtherMulticolor,
  IconPersonallyMulticolor,
  IconPhoneMulticolor,
} from '@blocket/blocket-ui'
import pulse from '@blocket/tracking-pulse'
import { useDecision, withOptimizely } from '@optimizely/react-sdk'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { actionCreators as selectAdInsertionActionCreators } from 'reduxBits/selectAdInsertion/actions'
import Card from './Card'

const oldAIURL = '/ai'
const loginURL = '/login'
const recomAICategoryURL = (catID) => { return `/mina-annonser/annonsera/ny?categoryID=${catID}` }
const bostadSlussUrl = '/mina-annonser/lagg-in-bostadsannons'
const mobilityAIURL = '/mina-annonser/fordon/'
const jobbURL = "https://jobb.blocket.se/rekrytering"
const pulsePageID = {
  'recomai': 'ai-draft-page',
  'oldai': 'old-ad-insertion',
  'bostad': 'blocketbostad',
  'jobb': 'blocketjobbse',
  'fordon': 'view-pick-fordon-category'
}

const categoryNames = {
  1000: 'Fordon',
  2000: 'För hemmet',
  3000: 'Bostad',
  4000: 'Personligt',
  5000: 'Elektronik',
  6000: 'Fritid & hobby',
  7000: 'Affärsverksamhet',
  8000: 'Övrigt',
  9000: 'Jobb'
}

const sendClickEvent = (redirectUri, redirectDomain, categoryID) => {
  let vertical
  if (redirectDomain === 'recomai') {
    vertical = 'generalist'
  } else if (redirectDomain === 'bostad') {
    vertical = 'real_estate'
  } 

  pulse.event({
    type: 'Engagement',
    action: 'Click',
    name: 'Select main category',
    object: {
      id: 'main-category-selection',
      type: 'UIElement',
      elementType: 'Button',
      category: categoryNames[categoryID],
      items: [{
        category: categoryNames[categoryID]
      }]
    },
    target: {
      id: pulsePageID[redirectDomain],
      type: 'Page',
      url: redirectUri,
    },
    vertical: {
      name: vertical
    }
  })
}

const mapDispatchToProps = (dispatch) => ({
  selectAdInsertionActions: bindActionCreators(selectAdInsertionActionCreators, dispatch),
})

const mapStateToProps = (state) => ({
  accountVerifiedByBankid: state.verification.status?.account?.verifiedByBankid,
  sessionVerifiedByBankid: state.verification.status?.session?.verifiedByBankid,
  userId: state.user.id,
})

const createCard = (id, icon, description = null) => {
  const CardComponent = ({
    selectAdInsertionActions,
    accountVerifiedByBankid,
    sessionVerifiedByBankid,
  }) => {
    const [decision] = useDecision('bank_id_verification_step2')
    return (
      <Card
        label={categoryNames[id]}
        description={description}
        price={0}
        icon={icon}
        onClick={(e) => {
          const redirectUri = recomAICategoryURL(id)
          sendClickEvent(redirectUri, 'recomai', id)
          if (decision.enabled && accountVerifiedByBankid && !sessionVerifiedByBankid) {
            selectAdInsertionActions.setVerificationModal(redirectUri)
            e.preventDefault()
          }
        }}
        link={recomAICategoryURL(id)}
      />
    )
  }
  CardComponent.propTypes = {
    selectAdInsertionActions: PropTypes.func,
    accountVerifiedByBankid: PropTypes.bool,
    sessionVerifiedByBankid: PropTypes.bool,
  }
  return CardComponent
}

const CarCardComponent = ({userId, optimizely}) => {
  let pulseId = 'oldai'
  let link = oldAIURL

  /**
   * In order to ensure users are bucketed correctly,
   * and that users get the same experience across all platforms,
   * we use the spid_id to check elegibiity.
   * To get a hold of the spid_id if users are not logged in,
   * redirect them to the login page.
   */
  if (userId) {
    const isFeatureEnabled = optimizely.isFeatureEnabled('mobility_new_ai', userId)

    if (isFeatureEnabled) {
      link = mobilityAIURL
      pulseId = 'fordon'
    }

  } else {
    const successUri = '/mina-annonser/lagg-in-annons'
    const cancelUri = '/mina-annonser/lagg-in-annons'
    
    link = `${loginURL}` +
    `?callbackId=desktop` +
    `&redirectUri=${encodeURIComponent(successUri)}` +
    `&cancelRedirectUri=${encodeURIComponent(cancelUri)}`
  }

  return (
    <Card
      label="Fordon"
      icon={<IconCarMulticolor />}
      onClick={() => sendClickEvent(link, pulseId, 1000)}
      link={link}
    />
  )
}

CarCardComponent.propTypes = {
  userId: PropTypes.string,
  optimizely: PropTypes.object,
}

export const HomeCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(2000, <IconChairMulticolor />)
)

export const ResidenceCardNew = () => {
  let platform = ''
  if (typeof window !== "undefined") {
    platform = new URLSearchParams(window.location.search).get('platform')
  }

  let url = bostadSlussUrl
  if (platform) {
    url = `${bostadSlussUrl}?platform=${platform}`
  }

  return (
    <Card
      label="Bostad"
      description="T.ex. lägenheter, utlandsboende eller fritidsboende"
      icon={<IconHouseMulticolor />}
      onClick={() => sendClickEvent(bostadSlussUrl, 'bostad', 3000)}
      link={url}
      price="från 0"
    />
  )
}

export const PersonalCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(4000, <IconPersonallyMulticolor />, "T.ex. kläder, skor, barnmöbler eller leksaker")
)

export const ElectronicCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(5000, <IconPhoneMulticolor />)
)

export const HobbyCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(6000, <IconHobbyMulticolor />, "T.ex. elcyklar, djur, musik- eller sportutrustning")
)

export const OtherCard = connect(mapStateToProps, mapDispatchToProps)(
  createCard(8000, <IconOtherMulticolor />)
)

export const CarCard = connect(mapStateToProps)(withOptimizely(CarCardComponent))

export const JobCard = () => (
  <Card
    label="Jobb"
    icon={<IconJobMulticolor />}
    onClick={() => sendClickEvent(jobbURL, 'jobb', 9000)}
    link={jobbURL}
  />
)

export const BusinessCard = () => {
  const [decision] = useDecision('recommerce_company_ai_for_apps')

  let affarsverksamhetTargetLink = oldAIURL
  if(decision.enabled) {
    if(decision.variables.affarsverksamhet_link_target) {
      affarsverksamhetTargetLink = decision.variables.affarsverksamhet_link_target
    }
  }
  return (
    <Card
      label={decision.enabled ? "Affärsverksamhet (företag)": "Affärsverksamhet"}
      description="T.ex. inventarier, maskiner, lokaler eller tjänster"
      icon={<IconBusinessMulticolor />}
      onClick={() => sendClickEvent(oldAIURL, 'oldai', 7000)}
      link={affarsverksamhetTargetLink}
    />
  )
}
